import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Copy, Download, Save, Loader2, Check, ChevronDown, ChevronUp } from "lucide-react";
import jsPDF from "jspdf";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StepGenerator = ({ 
  room, 
  generatorType, 
  stepNumber,
  title, 
  description, 
  fields,
  defaultOpen = false,
  onComplete
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [inputs, setInputs] = useState({});
  const [output, setOutput] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    // Check if this step was previously completed
    checkCompletion();
  }, []);

  const checkCompletion = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/progress`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.progress && data.progress[generatorType]) {
          setIsComplete(true);
        }
      }
    } catch (error) {
      console.error('Failed to check completion:', error);
    }
  };

  const handleInputChange = (fieldKey, value) => {
    setInputs(prev => ({ ...prev, [fieldKey]: value }));
  };

  const handleGenerate = async () => {
    const missingFields = fields.filter(f => f.required && !inputs[f.key]?.trim());
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setIsGenerating(true);
    setOutput("");

    try {
      const response = await fetch(`${API_URL}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          room,
          generator_type: generatorType,
          inputs
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Generation failed');
      }

      const data = await response.json();
      setOutput(data.output);
      
      // Mark step as complete
      await markComplete();
      
      toast.success("Your content has been crafted.");
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.message || "Something went wrong. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const markComplete = async () => {
    try {
      await fetch(`${API_URL}/api/user/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          step: generatorType,
          completed: true
        })
      });
      setIsComplete(true);
      if (onComplete) onComplete(generatorType);
    } catch (error) {
      console.error('Failed to mark complete:', error);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(output);
      toast.success("Copied to clipboard.");
    } catch (error) {
      toast.error("Failed to copy.");
    }
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    
    doc.setFont("times", "bold");
    doc.setFontSize(24);
    doc.setTextColor(44, 44, 44);
    doc.text(title, margin, 30);
    
    doc.setFont("times", "italic");
    doc.setFontSize(10);
    doc.setTextColor(107, 142, 124);
    doc.text(`Step ${stepNumber} • ${room}`, margin, 40);
    
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    doc.setTextColor(44, 44, 44);
    
    const lines = doc.splitTextToSize(output, maxWidth);
    let y = 55;
    
    lines.forEach(line => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(line, margin, y);
      y += 6;
    });
    
    doc.setFont("times", "italic");
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text("Generated by Calm Scale™ • The 14-Day Blueprint", margin, 285);
    
    doc.save(`Step_${stepNumber}_${title.replace(/\s+/g, '_')}.pdf`);
    toast.success("PDF downloaded.");
  };

  const handleSave = async () => {
    if (!output) return;
    
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/outputs/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          room,
          generator: generatorType,
          title,
          inputs,
          output
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save');
      }

      toast.success("Progress saved.");
    } catch (error) {
      toast.error("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div 
      className={`card-calm mb-6 animate-fade-up opacity-0 ${isComplete ? 'ring-1 ring-[#6B8E7C]/20' : ''}`} 
      style={{ animationFillMode: 'forwards', animationDelay: `${(stepNumber - 1) * 100}ms` }}
    >
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-left"
        data-testid={`step-toggle-${generatorType}`}
      >
        <div className="flex items-center gap-4">
          {/* Step Number */}
          <div className={`
            w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
            ${isComplete ? 'bg-[#6B8E7C] text-white' : 'bg-[#F2F0EB] text-[#2C2C2C]/60'}
            transition-all duration-300
          `}>
            {isComplete ? <Check className="w-4 h-4" /> : stepNumber}
          </div>
          <div>
            <h3 className="text-xl mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
              {title}
            </h3>
            <p className="text-sm text-[#2C2C2C]/60 font-light">
              {description}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isComplete && !isOpen && (
            <span className="text-xs text-[#6B8E7C] font-medium">Complete</span>
          )}
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-[#6B8E7C]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#6B8E7C]" />
          )}
        </div>
      </button>

      {/* Content */}
      {isOpen && (
        <div className="mt-8 pt-8 border-t border-[#E5E0D6]">
          {/* Input Fields */}
          <div className="space-y-6 mb-8">
            {fields.map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-[#2C2C2C]/80 mb-2">
                  {field.label}
                  {field.required && <span className="text-[#D4A373] ml-1">*</span>}
                </label>
                {field.type === 'textarea' ? (
                  <Textarea
                    placeholder={field.placeholder}
                    value={inputs[field.key] || ''}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    className="input-calm min-h-[100px] resize-y border border-[#E5E0D6] rounded-xl p-4"
                    data-testid={`input-${field.key}`}
                  />
                ) : (
                  <input
                    type="text"
                    placeholder={field.placeholder}
                    value={inputs[field.key] || ''}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    className="input-calm"
                    data-testid={`input-${field.key}`}
                  />
                )}
                {field.hint && (
                  <p className="text-xs text-[#2C2C2C]/40 mt-1">{field.hint}</p>
                )}
              </div>
            ))}
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="btn-primary w-full mb-8"
            data-testid={`generate-btn-${generatorType}`}
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                Crafting your content...
              </>
            ) : (
              'Reveal Insights'
            )}
          </Button>

          {/* Output */}
          {output && (
            <div className="animate-fade-in">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-[#6B8E7C] uppercase tracking-wide font-medium">
                  Your Output
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    className="btn-ghost text-xs"
                    data-testid={`copy-btn-${generatorType}`}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDownloadPDF}
                    className="btn-ghost text-xs"
                    data-testid={`download-btn-${generatorType}`}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    PDF
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="btn-ghost text-xs"
                    data-testid={`save-btn-${generatorType}`}
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-1" />
                    )}
                    Save
                  </Button>
                </div>
              </div>
              <div 
                className="p-6 bg-white rounded-xl border border-[#E5E0D6] output-document"
                data-testid={`output-${generatorType}`}
              >
                {output}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
